// Features: M004-F0807, M004-F0808 (WebUI #270, slice B1: the calendar update data layer)
// Tests: T-M004-0466 (model and api rows; see src/services/calendarUpdateApi.test.js)
//
// The calendar update client, isolated in this one module. Spec core-ledger-account-gateway 1.29.0 (merged Spec !140, Spec main 73f28342; the pin
// is tests/ledger-account-gateway/contract-pin.json). Every operation goes through Core's generic relay (CSLPT):
// POST /api/v1/orgs/:orgId/modules/ledger/messages with the closed envelope { relay_version: 1, type, payload, target_user_id? }.
// Core forwards `payload` unread to the Ledger as the frame body.
//
// Frame mapping, from the pinned openapi (components ProposalListBody, ProposalAcceptBody, ProposalLinkReplacementBody,
// CorrectionReviewGetBody) and interface.json frameOperations 58..61:
//   proposal.list: the body members are the filters (tx_id, proposal_id, customer_user_id, state, sort, direction, limit, cursor).
//     The customer is a BODY member (customer_user_id). No target_user_id: the proposal is not a customer frame.
//   proposal.accept: body { proposal_id, proposal_revision, reason } (all three required, reason 1..1000 chars, one non-space).
//   proposal.link_replacement: body { proposal_id, proposal_revision, decision: link|separate, reason } (reason required by the
//     contract even though the design sketch omitted it). No target_user_id.
//   correction_review.get: body { review_id }. No target_user_id.
//
// Writes (accept, link_replacement) send an Idempotency-Key header, the frame-level key. The caller generates one per user action
// and reuses it on every retry of that action; this module refuses to send a write without one (no request is made).
//
// Answers keep the typed shape the components read: { ...response, data: { success: true, data: <Ledger data> } }.
import api from './api';

const messagesPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
const envelope = (type, payload) => ({ relay_version: 1, type, payload });
const keyed = (idempotencyKey) => ({ headers: { 'Idempotency-Key': idempotencyKey } });
const typedAnswer = (response) => ({
  ...response,
  data: { success: true, data: response?.data?.data?.payload?.data },
});

// Copies only the members that are present, so the body has no undefined-valued keys.
const present = (members) => Object.fromEntries(Object.entries(members).filter(([, v]) => v !== undefined));

const requireKey = (idempotencyKey) => {
  if (!idempotencyKey) throw new Error('An idempotency key is required to accept a calendar update');
  return idempotencyKey;
};

export const calendarUpdateApi = {
  // READ (SAL 3). Filters: customer_user_id, state (pending default | accepted | superseded | declined | any), sort, direction,
  // limit, cursor, proposal_id, tx_id. The reply carries pending_count, which ignores state and paging.
  list: async (orgId, { customer_user_id: customerUserId, state, sort, direction, limit, cursor, proposal_id: proposalId, tx_id: txId } = {}) => typedAnswer(
    await api.post(messagesPath(orgId), envelope('proposal.list', present({
      customer_user_id: customerUserId, state, sort, direction, limit, cursor, proposal_id: proposalId, tx_id: txId,
    }))),
  ),

  // WRITE (SAL 4). proposalRevision is the revision on screen. The same idempotencyKey is sent on every retry of one accept.
  accept: async (orgId, { proposal_id: proposalId, proposal_revision: proposalRevision, reason }, idempotencyKey) => {
    const key = requireKey(idempotencyKey);
    return typedAnswer(await api.post(
      messagesPath(orgId),
      envelope('proposal.accept', { proposal_id: proposalId, proposal_revision: proposalRevision, reason }),
      keyed(key),
    ));
  },

  // WRITE (SAL 4). decision: 'link' | 'separate'. A link decision bumps proposal_revision; re-read before accepting.
  linkReplacement: async (orgId, {
    proposal_id: proposalId, proposal_revision: proposalRevision, decision, reason,
  }, idempotencyKey) => {
    const key = requireKey(idempotencyKey);
    return typedAnswer(await api.post(
      messagesPath(orgId),
      envelope('proposal.link_replacement', {
        proposal_id: proposalId, proposal_revision: proposalRevision, decision, reason,
      }),
      keyed(key),
    ));
  },

  // READ (SAL 3). review_id comes from the accept result's correction_review handle.
  correctionReviewGet: async (orgId, reviewId) => typedAnswer(
    await api.post(messagesPath(orgId), envelope('correction_review.get', { review_id: reviewId })),
  ),
};
